import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Switch,
  Descriptions,
  Alert,
  Spin,
  Badge,
} from 'antd';
import {
  ApiOutlined,
  ReloadOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BugOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchChannels,
  fetchChannelDetail,
  updateChannelConfig,
  testChannel,
  fetchChannelRequirements,
  enableChannel,
  disableChannel,
} from '../store/slices/channelSlice';

const { Title, Text, Paragraph, Link } = Typography;

const ChannelsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [configForm] = Form.useForm();
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [requirementsModalVisible, setRequirementsModalVisible] = useState(false);
  const [testResultModalVisible, setTestResultModalVisible] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);

  const {
    channels,
    currentChannel,
    requirements,
    testResult,
    updating,
    testing,
    error,
  } = useAppSelector((state) => state.channel);

  useEffect(() => {
    dispatch(fetchChannels());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleViewDetail = async (id: string) => {
    try {
      await dispatch(fetchChannelDetail(id)).unwrap();
      setDetailModalVisible(true);
    } catch (err: any) {
      message.error(err || '获取渠道详情失败');
    }
  };

  const handleConfigChannel = async (id: string) => {
    try {
      await dispatch(fetchChannelDetail(id)).unwrap();
      setSelectedChannelId(id);
      // Pre-fill form with existing config
      const channel = channels.data?.find((c) => c.id === id);
      if (channel?.config) {
        configForm.setFieldsValue({
          config: JSON.stringify(channel.config, null, 2),
        });
      }
      setConfigModalVisible(true);
    } catch (err: any) {
      message.error(err || '获取渠道信息失败');
    }
  };

  const handleUpdateConfig = async (values: any) => {
    if (!selectedChannelId) return;

    try {
      const config = JSON.parse(values.config);
      await dispatch(
        updateChannelConfig({ id: selectedChannelId, config })
      ).unwrap();
      message.success('渠道配置已更新');
      setConfigModalVisible(false);
      setSelectedChannelId(null);
      configForm.resetFields();
      dispatch(fetchChannels());
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        message.error('配置JSON格式错误');
      } else {
        message.error(err || '更新配置失败');
      }
    }
  };

  const handleTestChannel = async (id: string) => {
    try {
      await dispatch(testChannel(id)).unwrap();
      setTestResultModalVisible(true);
    } catch (err: any) {
      message.error(err || '测试失败');
      setTestResultModalVisible(true);
    }
  };

  const handleViewRequirements = async (id: string) => {
    try {
      await dispatch(fetchChannelRequirements(id)).unwrap();
      setRequirementsModalVisible(true);
    } catch (err: any) {
      message.error(err || '获取渠道要求失败');
    }
  };

  const handleToggleChannel = async (id: string, enabled: boolean) => {
    try {
      if (enabled) {
        await dispatch(disableChannel(id)).unwrap();
        message.success('渠道已禁用');
      } else {
        await dispatch(enableChannel(id)).unwrap();
        message.success('渠道已启用');
      }
    } catch (err: any) {
      message.error(err || '操作失败');
    }
  };

  const handleRefresh = () => {
    dispatch(fetchChannels());
  };

  const getProviderIcon = (providerType: string) => {
    // You can add custom icons for different providers
    return <ApiOutlined style={{ fontSize: 32, color: '#2F6BFF' }} />;
  };

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          支付渠道配置
        </Title>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
          刷新
        </Button>
      </div>

      <Spin spinning={channels.loading}>
        <Row gutter={[16, 16]}>
          {channels.data?.map((channel) => (
            <Col xs={24} sm={12} lg={8} key={channel.id}>
              <Badge.Ribbon
                text={channel.enabled ? '已启用' : '已禁用'}
                color={channel.enabled ? 'green' : 'default'}
              >
                <Card
                  hoverable
                  style={{ height: '100%' }}
                  actions={[
                    <Button
                      type="link"
                      icon={<SettingOutlined />}
                      onClick={() => handleConfigChannel(channel.id)}
                    >
                      配置
                    </Button>,
                    <Button
                      type="link"
                      icon={<BugOutlined />}
                      onClick={() => handleTestChannel(channel.id)}
                      loading={testing}
                    >
                      测试
                    </Button>,
                    <Button
                      type="link"
                      icon={<FileTextOutlined />}
                      onClick={() => handleViewRequirements(channel.id)}
                    >
                      要求
                    </Button>,
                  ]}
                >
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {channel.icon ? (
                      <img
                        src={channel.icon}
                        alt={channel.name}
                        style={{ width: 48, height: 48 }}
                      />
                    ) : (
                      getProviderIcon(channel.provider_type)
                    )}
                  </div>

                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ marginBottom: 4 }}>
                      {channel.name}
                    </Title>
                    <Text type="secondary">{channel.provider_type}</Text>
                  </div>

                  {channel.description && (
                    <Paragraph
                      ellipsis={{ rows: 2 }}
                      style={{ marginBottom: 16, minHeight: 44 }}
                    >
                      {channel.description}
                    </Paragraph>
                  )}

                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text strong>状态：</Text>
                      <Switch
                        checked={channel.enabled}
                        onChange={() =>
                          handleToggleChannel(channel.id, channel.enabled)
                        }
                        loading={updating}
                        checkedChildren="启用"
                        unCheckedChildren="禁用"
                      />
                    </div>

                    <div>
                      <Text strong>支付方式：</Text>
                      <div style={{ marginTop: 4 }}>
                        {channel.payment_methods && channel.payment_methods.length > 0 ? (
                          <>
                            {channel.payment_methods.slice(0, 6).map((m) => (
                              <Tag key={m} style={{ marginBottom: 4 }}>
                                {m}
                              </Tag>
                            ))}
                            {channel.payment_methods.length > 6 && (
                              <Tag>+{channel.payment_methods.length - 6}</Tag>
                            )}
                          </>
                        ) : (
                          <Text type="secondary">-</Text>
                        )}
                      </div>
                    </div>

                    {channel.supported_currencies && channel.supported_currencies.length > 0 && (
                      <div>
                        <Text strong>支持币种：</Text>
                        <div style={{ marginTop: 4 }}>
                          {channel.supported_currencies.slice(0, 5).map((currency) => (
                            <Tag key={currency} style={{ marginBottom: 4 }}>
                              {currency}
                            </Tag>
                          ))}
                          {channel.supported_currencies.length > 5 && (
                            <Tag>+{channel.supported_currencies.length - 5}</Tag>
                          )}
                        </div>
                      </div>
                    )}

                    <Button
                      type="link"
                      block
                      onClick={() => handleViewDetail(channel.id)}
                    >
                      查看详情
                    </Button>
                  </Space>
                </Card>
              </Badge.Ribbon>
            </Col>
          ))}
        </Row>

        {channels.data?.length === 0 && (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <ApiOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <Title level={4} type="secondary" style={{ marginTop: 16 }}>
                暂无支付渠道
              </Title>
            </div>
          </Card>
        )}
      </Spin>

      {/* Config Modal */}
      <Modal
        title="配置渠道"
        open={configModalVisible}
        onCancel={() => {
          setConfigModalVisible(false);
          setSelectedChannelId(null);
          configForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Alert
          message="配置说明"
          description="请输入有效的JSON格式配置。配置项根据不同的支付渠道而异，请参考渠道文档。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={configForm} layout="vertical" onFinish={handleUpdateConfig}>
          <Form.Item
            label="配置（JSON）"
            name="config"
            rules={[
              { required: true, message: '请输入配置' },
              {
                validator: (_, value) => {
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch (e) {
                    return Promise.reject('请输入有效的JSON格式');
                  }
                },
              },
            ]}
          >
            <Input.TextArea
              rows={12}
              placeholder='{\n  "api_key": "your_api_key",\n  "secret_key": "your_secret_key",\n  "webhook_url": "https://example.com/webhook"\n}'
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setConfigModalVisible(false);
                  setSelectedChannelId(null);
                  configForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={updating}>
                保存配置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="渠道详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {currentChannel && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="渠道ID" span={2}>
              <Text copyable>{currentChannel.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="渠道名称">
              {currentChannel.name}
            </Descriptions.Item>
            <Descriptions.Item label="提供商类型">
              {currentChannel.provider_type}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={currentChannel.enabled ? 'success' : 'default'}>
                {currentChannel.enabled ? '已启用' : '已禁用'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="支持币种" span={2}>
              {currentChannel.supported_currencies?.map((currency) => (
                <Tag key={currency}>{currency}</Tag>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="支持国家" span={2}>
              {currentChannel.supported_countries?.map((country) => (
                <Tag key={country}>{country}</Tag>
              ))}
            </Descriptions.Item>
            {currentChannel.description && (
              <Descriptions.Item label="描述" span={2}>
                {currentChannel.description}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="当前配置" span={2}>
              <pre
                style={{
                  margin: 0,
                  background: '#f5f5f5',
                  padding: 12,
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(currentChannel.config, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Requirements Modal */}
      <Modal
        title="渠道配置要求"
        open={requirementsModalVisible}
        onCancel={() => setRequirementsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRequirementsModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {requirements && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card title="必填字段" size="small">
              {requirements.required_fields?.length > 0 ? (
                <Space wrap>
                  {requirements.required_fields.map((field) => (
                    <Tag key={field} color="red">
                      {field}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">无必填字段</Text>
              )}
            </Card>

            <Card title="可选字段" size="small">
              {requirements.optional_fields?.length > 0 ? (
                <Space wrap>
                  {requirements.optional_fields.map((field) => (
                    <Tag key={field}>{field}</Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">无可选字段</Text>
              )}
            </Card>

            {requirements.documentation_url && (
              <Alert
                message="查看文档"
                description={
                  <Link
                    href={requirements.documentation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {requirements.documentation_url}
                  </Link>
                }
                type="info"
                showIcon
              />
            )}
          </Space>
        )}
      </Modal>

      {/* Test Result Modal */}
      <Modal
        title="测试结果"
        open={testResultModalVisible}
        onCancel={() => setTestResultModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTestResultModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        {testResult && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              message={testResult.success ? '测试成功' : '测试失败'}
              description={testResult.message}
              type={testResult.success ? 'success' : 'error'}
              showIcon
              icon={
                testResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />
              }
            />

            {testResult.details && (
              <Card title="详细信息" size="small">
                <pre
                  style={{
                    margin: 0,
                    background: '#f5f5f5',
                    padding: 12,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(testResult.details, null, 2)}
                </pre>
              </Card>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default ChannelsPage;
